// components/SignUpOptions.tsx
'use client';

import React, { useState } from 'react';
import { FcGoogle } from 'react-icons/fc';
import { FaApple } from 'react-icons/fa';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { useNativeApp } from '@/components/runtime/native-app-context';
import { getBridge } from '@/lib/native/bridge';

// The firebase SDK is imported inside the sign-in handlers: this component is
// statically reachable from always-rendered UI (profile button), so a
// module-scope import would put the whole auth SDK in the initial bundle.

export const SignInOptions = ({ locale }: { locale: LocaleDetails }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { isNativeShell, platform } = useNativeApp();

  const handleGoogleSignIn = async () => {
    try {
      const [{ auth }, { GoogleAuthProvider, signInWithPopup }] = await Promise.all([
        import('@/lib/firebase'),
        import('firebase/auth'),
      ]);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // Refresh page after successful sign-in
      window.location.reload();
    } catch (error: any) {
      setError(error.message);
    }
  };

  /**
   * Native shell sign-in: signInWithPopup is hard-broken inside WebViews
   * (Google rejects embedded user agents), so the shell runs the NATIVE
   * provider flow and hands back {idToken, rawNonce}; the Firebase JS session
   * is then established with signInWithCredential — everything downstream
   * (auth-context onAuthStateChanged, the AES uid cookie) works unchanged.
   */
  const handleNativeSignIn = async (providerId: 'apple' | 'google') => {
    try {
      const bridge = getBridge();
      if (!bridge) throw new Error('Native bridge unavailable');
      const { idToken, rawNonce } = await bridge.signIn(providerId);
      const [{ auth }, { OAuthProvider, GoogleAuthProvider, signInWithCredential }] =
        await Promise.all([import('@/lib/firebase'), import('firebase/auth')]);
      const credential =
        providerId === 'apple'
          ? new OAuthProvider('apple.com').credential({ idToken, rawNonce })
          : GoogleAuthProvider.credential(idToken);
      await signInWithCredential(auth, credential);
      window.location.reload();
    } catch (err: any) {
      if (err?.message === 'google_native_signin_not_available') {
        setError('Google sign-in is coming to the app soon — use Apple for now.');
      } else {
        setError(err.message ?? String(err));
      }
    }
  };

  const handleEmailLinkSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const actionCodeSettings = {
      url: `${window.location.origin}/${locale.path}/auth/confirm`,
      handleCodeInApp: true,
    };

    try {
      const [{ auth }, { sendSignInLinkToEmail }] = await Promise.all([
        import('@/lib/firebase'),
        import('firebase/auth'),
      ]);
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      setMessage('A sign-in link has been sent to your email.');
      // Note: Page will refresh when user clicks the email link and completes sign-in
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="flex flex-col items-center w-full">
      {/* Title - Gemini style */}
      <h2 className="text-2xl font-normal text-gray-900 dark:text-gray-100 mb-8 text-center">
        Sign in to continue
      </h2>

      {/* Native shell: Sign in with Apple is MANDATORY next to any social
          login (App Store 4.8) and shown first on iOS. Email-link is hidden
          in-app (the link opens in the browser, not the shell). */}
      {isNativeShell && platform === 'ios' && (
        <button
          onClick={() => handleNativeSignIn('apple')}
          className="w-full mb-3 flex items-center justify-center gap-3 px-5 py-3.5 rounded-full bg-black text-white dark:bg-white dark:text-black transition-all duration-200 shadow-sm hover:shadow-md"
        >
          <FaApple size={22} className="flex-shrink-0" />
          <span className="font-medium text-[15px]">Continue with Apple</span>
        </button>
      )}

      {/* Google Sign-In Button - Gemini-inspired clean design */}
      <button
        onClick={() => (isNativeShell ? handleNativeSignIn('google') : handleGoogleSignIn())}
        className="w-full flex items-center justify-center gap-3 px-5 py-3.5 border border-gray-200 dark:border-gray-700/80 rounded-full bg-white dark:bg-dark/50 hover:bg-gray-50 dark:hover:bg-dark-inside transition-all duration-200 shadow-sm hover:shadow-md group"
      >
        <FcGoogle size={22} className="flex-shrink-0" />
        <span className="font-medium text-[15px] text-gray-800 dark:text-gray-200">Continue with Google</span>
      </button>

      {error && isNativeShell && (
        <div className="mt-4 w-full text-red-600 dark:text-red-400 text-sm text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-800/40">
          {error}
        </div>
      )}

      {!isNativeShell && (
        <>
          {/* Divider - Gemini style minimal */}
          <div className="flex items-center w-full my-7">
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700/60" />
            <span className="px-4 text-sm text-gray-500 dark:text-gray-500 font-normal">or</span>
            <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700/60" />
          </div>

          {/* Email Link Sign-In Form - Gemini-inspired */}
          <form onSubmit={handleEmailLinkSignIn} className="w-full flex flex-col space-y-4">
            {error && (
              <div className="text-red-600 dark:text-red-400 text-sm text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 dark:border-red-800/40">
                {error}
              </div>
            )}
            {message && (
              <div className="text-green-700 dark:text-green-400 text-sm text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-2xl border border-green-100 dark:border-green-800/40">
                {message}
              </div>
            )}
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full px-5 py-3.5 border border-gray-200 dark:border-gray-700/80 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500/40 dark:focus:ring-blue-400/40 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-dark/50 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 transition-all duration-200 text-[15px]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <button
              type="submit"
              className="w-full px-5 py-3.5 text-white bg-gradient-to-r from-blue-600 to-blue-500 dark:from-blue-600 dark:to-blue-500 rounded-full hover:from-blue-700 hover:to-blue-600 dark:hover:from-blue-700 dark:hover:to-blue-600 transition-all duration-200 shadow-sm hover:shadow-md font-medium text-[15px]"
            >
              Send sign-in link
            </button>
          </form>
        </>
      )}

      {/* Privacy note - Gemini style subtle text */}
      <p className="mt-6 text-xs text-center text-gray-500 dark:text-gray-500 leading-relaxed max-w-xs">
        By continuing, you agree to our{' '}
        <a href={`/${locale.path}/terms`} className="underline hover:opacity-80">
          Terms of Use
        </a>{' '}
        and{' '}
        <a href={`/${locale.path}/privacy`} className="underline hover:opacity-80">
          Privacy Policy
        </a>
      </p>
    </div>
  );
};
